import { useMemo } from 'react';
import type { ProcessRunListItemDto } from '@api/processRuns';
import type { RunHistoryStats } from '../editor/dock/RunHistoryAnalytics';

const DAY_MS = 86_400_000;

function buildBins(
  runs: readonly ProcessRunListItemDto[],
  nowMs: number,
): number[] {
  const bins = new Array<number>(14).fill(0);
  for (const run of runs) {
    const ts = run.startedAt ?? run.createdAt;
    if (!ts) continue;
    const daysAgo = Math.floor((nowMs - new Date(ts).getTime()) / DAY_MS);
    if (daysAgo < 0 || daysAgo > 13) continue;
    bins[13 - daysAgo] += 1;
  }
  return bins;
}

/**
 * Derive the {@link RunHistoryStats} the sidebar analytics strip needs
 * from a flat list of runs. Pure computation — no fetches.
 *
 * Returns `null` when the list is empty so the analytics card renders
 * its "no runs yet" empty state.
 */
export function useRunHistoryStats(
  runs: readonly ProcessRunListItemDto[],
  nowMs: number,
): RunHistoryStats | null {
  return useMemo(() => {
    if (runs.length === 0) return null;

    const totalRuns = runs.length;

    const terminalRuns = runs.filter(
      (r) =>
        r.status === 'completed' ||
        r.status === 'failed' ||
        r.status === 'cancelled',
    );
    const succeeded = runs.filter((r) => r.status === 'completed').length;
    const successRate =
      terminalRuns.length > 0
        ? Math.round((succeeded / terminalRuns.length) * 100)
        : 0;

    const durations = runs
      .map((r) => r.durationMs)
      .filter((d): d is number => d != null && d > 0)
      .sort((a, b) => a - b);

    let medianDurationMs = 0;
    if (durations.length > 0) {
      const mid = Math.floor(durations.length / 2);
      medianDurationMs =
        durations.length % 2 === 1
          ? durations[mid]
          : Math.round((durations[mid - 1] + durations[mid]) / 2);
    }

    const last14Days = buildBins(runs, nowMs);

    return { totalRuns, successRate, medianDurationMs, last14Days };
  }, [runs, nowMs]);
}
